import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { meKeys } from '@/features/settings/api/meKeys';
import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

const schema = z.array(z.string());

const permissionKeys = {
  mine: (tenantId: string | null) => ['permissions', 'mine', tenantId] as const,
};

// Returns a hasPermission(code) predicate for the current user in their
// active tenant. Falls back to "allow all" while the query is in-flight so
// the sidebar doesn't flash empty on first load.
//
// Side effect: the first time permissions load, invalidate meKeys.detail().
// This ensures the profile card reflects the Studio membership that
// TryAutoProvisionAsync may have just created on this permissions call.
export function useMyPermissions(): (code: string) => boolean {
  const tenantId = useTenantStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const invalidatedRef = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey: permissionKeys.mine(tenantId),
    queryFn: async ({ signal }) => {
      const raw = await apiClient.get(`api/v1/t/${tenantId}/me/permissions`, { signal }).json();
      return schema.parse(raw);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // When permissions data first arrives, refresh the profile. TryAutoProvisionAsync
  // runs inside the permissions endpoint and may have just created the Studio
  // membership/user shadow — without this invalidation, /api/v1/me would stay
  // stale showing null names and null roleName until the next page load.
  useEffect(() => {
    if (data !== undefined && !invalidatedRef.current) {
      invalidatedRef.current = true;
      queryClient.invalidateQueries({ queryKey: meKeys.detail() });
    }
  }, [data, queryClient]);

  if (!tenantId) return () => false;
  if (isLoading || !data) return () => true;

  const set = new Set(data);
  return (code: string) => set.has(code);
}
